import { Box, Button } from '@deckee/deck-hand';
import React, { useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import styled from 'styled-components';
import { captureEvent } from '../../../../services/event-capture';
import { addIncident } from '../../../../services/incidents';
import captureSpecificError from '../../../../services/sentry';
import { ROUTE_INCIDENT_REPORT } from '../../../../services/route/route-names';
import { transitionToNextRoute } from '../../../../services/route/route-navigation';
import {
  setBaseMap,
  showPopover,
} from '../../../../services/store/modules/app-controls';
import {
  selectIncidentDescription,
  selectIncidentDetails,
} from '../../../../services/store/modules/incident-report';
import BasicDrawerLayout from '../../../layouts/BasicDrawerLayout/BasicDrawerLayout';
import PopOverMessage from '../../../shared/PopOverMessage/PopOverMessage';
import ErrorMessage from '../../../shared/ErrorMessage/ErrorMessage';
import ContactDetailsForm from '../components/ContactDetailsForm/ContactDetailsForm';
import useIncidentReportingLocation from '../hooks/useIncidentReportingLocation';

const StyledErrorMessage = styled(ErrorMessage)`
  margin-bottom: 20px;
`;

const ContactDetailsPage = () => {
  const dispatch = useDispatch();
  const history = useHistory();

  const [error, setError] = useState<{
    message: string;
    retryActionLabel?: string;
    retryAction?: () => void;
  } | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { incidentDateTime, tags, type } = useSelector(selectIncidentDetails);

  const {
    vesselRegistration,
    description,
    imageUrls,
    videoUploads,
    offlineInitiated,
  } = useSelector(selectIncidentDescription);

  const handleBack = async () => {
    await captureEvent({
      type: 'incident: back to incident description',
      payload: {},
      sendToAmplitude: true,
    });

    history.goBack();
  };

  const onSuccess = useCallback(() => {
    // close the side bar and show the map
    dispatch(setBaseMap());

    // Show the popover message feedback
    dispatch(
      showPopover(
        <PopOverMessage details="If required, a member of our team or the relevant authorities will reach out to you in approximately five business days." />,
        'Your report has been received',
      ),
    );

    // close the success message and route to map
    setTimeout(() => {
      history.push('/maps');
    }, 10000);
  }, [dispatch, history]);

  const [selectedLatitude, selectedLongitude] = useIncidentReportingLocation(
    () => {
      setError({
        message: 'The location selected is not valid.',
        retryActionLabel: 'Edit location',
        retryAction: () => transitionToNextRoute(ROUTE_INCIDENT_REPORT),
      });
    },
  );

  const submitHandler = useCallback(
    async (formValues) => {
      try {
        setIsSubmitting(true);

        const payload = {
          latitude: selectedLatitude,
          longitude: selectedLongitude,
          incidentDateTime,
          vesselRegistration,
          imageUrls,
          description,
          offlineInitiated,
          tags,
          type,
          videoUploads,
          ...formValues,
        };

        await addIncident(payload);
        await captureEvent({
          type: 'incident: submit',
          payload: {
            ...formValues,
            selectedLatitude,
            selectedLongitude,
          },
          sendToAmplitude: true,
        });

        onSuccess();
      } catch (err) {
        captureSpecificError(
          err,
          'containers/app-main-client/src/components/features/IncidentReport/ContactDetailsPage/ContactDetailsPage.jsx',
        );
        setError({
          message: 'Your incident report could not be saved.',
        });
      } finally {
        setIsSubmitting(false);
      }
    },
    [
      selectedLatitude,
      selectedLongitude,
      incidentDateTime,
      vesselRegistration,
      imageUrls,
      description,
      tags,
      type,
      videoUploads,
      onSuccess,
    ],
  );

  return (
    <BasicDrawerLayout
      showMapOverlay
      heading="Report an incident"
      onBack={handleBack}
      loading={isSubmitting}
      showMenuBar={false}
    >
      {error && (
        <Box p={3} bg="white">
          <StyledErrorMessage
            description={error?.message || 'There was an error.'}
          />

          {error?.retryAction && error?.retryActionLabel && (
            <Button onClick={error.retryAction} wide>
              {error.retryActionLabel}
            </Button>
          )}
        </Box>
      )}

      {selectedLatitude && selectedLongitude && !error ? (
        <ContactDetailsForm onSubmit={submitHandler} />
      ) : null}
    </BasicDrawerLayout>
  );
};

export default ContactDetailsPage;
